// src/pages/ingresos/index.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "../../utils/supabase";
import QRLabelModal from "../../components/QRLabelModal.jsx";
import { scrollManager } from "../../utils/scrollManager"; // ✅ Importar el gestor

// Componentes
import POFilters from "./components/POFilters";
import InstrumentQuickSearch from "./components/InstrumentQuickSearch";
import POTable from "./components/POTable";
import AssignTechnicianModal from "./components/AssignTechnicianModal";
import RemitosModal from "./components/RemitosModal";
import OperationsModal from "./components/OperationsModal";
import CreateIntakeModal from "./components/CreateIntakeModal";
import IntakeDrawer from "./components/IntakeDrawer";
import LinkIntakeToPOModal from "./components/LinkIntakeToPOModal";
import IntakeInstrumentModal from "./components/IntakeInstrumentModal";
import PendingIntakeInstrumentsModal from "./components/PendingIntakeInstrumentsModal";
import InstrumentSearchModal from "./components/InstrumentSearchModal";
import { useLabPermissions } from "../../hooks/useLabPermissions";
import { ASSISTANT_PERMISSION_KEYS } from "../../utils/assistantPermissions";
import {
  isPreviousCycleStatus,
  resolveAdmissionReceptionDate,
  resolveAdmissionStatus,
} from "../../utils/instrument-admission";

// ---- UI helpers ----
const Badge = ({ children, color = "slate" }) => {
  const BADGE_MAP = {
    slate: "bg-slate-800/20 text-slate-300 border border-slate-700/40",
    sky: "bg-sky-800/20 text-sky-300 border border-sky-700/40",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        BADGE_MAP[color] || BADGE_MAP.slate
      }`}
    >
      {children}
    </span>
  );
};

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === "error" ? "bg-red-900/90" : "bg-emerald-900/90";
  const borderColor = type === "error" ? "border-red-700" : "border-emerald-700";

  return (
    <div
      className={`fixed top-4 right-4 z-50 ${bgColor} ${borderColor} border rounded-lg px-4 py-3 shadow-lg max-w-md`}
    >
      <div className="flex items-center gap-2">
        <span className="text-sm text-white">{message}</span>
        <button onClick={onClose} className="ml-2 text-white/80 hover:text-white">
          x
        </button>
      </div>
    </div>
  );
};

// --- Helpers ---
function normalizeScanPayload(raw) {
  const s = String(raw).trim();
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      const parts = u.pathname.split("/").filter(Boolean);
      const last = parts[parts.length - 1];
      if (last) return last;
    } catch {}
  }
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s)
  )
    return s;
  if (/^\d+$/.test(s)) return s;
  return s;
}

function osNumeric(code) {
  if (!code) return 0;
  const digits = String(code).replace(/\D/g, "");
  const n = parseInt(digits, 10);
  return Number.isNaN(n) ? 0 : n;
}

function poNumeric(po) {
  const base = parseInt(String(po?.base_number ?? ""), 10);
  if (Number.isFinite(base)) return base;
  return osNumeric(po?.code);
}

function normText(v) {
  return String(v || "").toLowerCase().trim();
}

function toIntOrNull(v) {
  const n = Number.parseInt(String(v ?? "").trim(), 10);
  return Number.isNaN(n) ? null : n;
}

function isUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || "").trim()
  );
}

function sanitizeIlikeTerm(value) {
  return String(value || "")
    .trim()
    .replace(/[%,]/g, " ")
    .replace(/\s+/g, " ");
}

const LEGACY_STATUS_TO_NEW = {
  pending: "delivered_to_lab",
  in_progress: "calibration_finished",
  completed: "client_notified",
  blocked: "uploaded_for_remito",
};

const normalizeServiceOrderStatus = (status) => {
  const s = String(status || "").trim();
  if (!s) return "delivered_to_lab";
  if (
    [
      "delivered_to_lab",
      "calibration_finished",
      "certificates_finished_controlled",
      "uploaded_for_remito",
      "remito_generated",
      "client_notified",
    ].includes(s)
  ) {
    return s;
  }
  return LEGACY_STATUS_TO_NEW[s] || "delivered_to_lab";
};


// --- Anclaje de scroll (marcador por instrumento/OS) ---
// Tras cambiar el estado de un instrumento (o editarlo), el refresh de la
// lista hace que algunos navegadores salten el scroll al inicio de la página.
// En vez de recordar la posición en píxeles (frágil: al remontarse la lista
// cambian las alturas), "clavamos" la fila del instrumento (o su OS) por su
// ID en el DOM y la mantenemos en el mismo lugar de la pantalla durante unos
// segundos, corrigiendo en cada re-render. El usuario vuelve exactamente al
// instrumento que estaba tocando. Si el usuario scrollea a mano, se cancela.
function pinScrollAnchor({ instrumentId, poId }, durationMs = 3000) {
  const selectorFor = () => {
    if (instrumentId) {
      const el = document.querySelector(`[data-instrument-id="${instrumentId}"]`);
      if (el) return el;
    }
    if (poId) return document.querySelector(`[data-po-id="${poId}"]`);
    return null;
  };

  const anchor = selectorFor();
  if (!anchor) return;

  // Offset dentro del viewport que queremos preservar (a qué altura de la
  // pantalla estaba la fila cuando el usuario hizo el cambio).
  const targetTop = anchor.getBoundingClientRect().top;

  let cancelled = false;
  const cancel = () => {
    cancelled = true;
  };
  // Solo el scroll HUMANO cancela (rueda/touch/teclado). El salto espurio del
  // navegador dispara "scroll" pero no estos gestos.
  window.addEventListener("wheel", cancel, { passive: true });
  window.addEventListener("touchmove", cancel, { passive: true });
  window.addEventListener("keydown", cancel, true);

  const correct = () => {
    if (cancelled) return;
    const el = selectorFor();
    if (!el) return;
    const diff = el.getBoundingClientRect().top - targetTop;
    if (Math.abs(diff) > 2) {
      window.scrollBy(0, diff);
    }
  };

  // Corregir en cada cambio del DOM de la lista (el salto ocurre al remontar).
  const obs = new MutationObserver(() => {
    requestAnimationFrame(correct);
  });
  obs.observe(document.body, { childList: true, subtree: true });

  // Y correcciones cronometradas para los re-render asíncronos del refresh.
  const timers = [30, 120, 250, 450, 700, 1000, 1500, 2200, 2900].map((t) =>
    setTimeout(correct, t)
  );

  setTimeout(() => {
    obs.disconnect();
    timers.forEach(clearTimeout);
    window.removeEventListener("wheel", cancel);
    window.removeEventListener("touchmove", cancel);
    window.removeEventListener("keydown", cancel, true);
  }, durationMs);
}

// --- Persistencia de filtros de Ingresos ---
// Los filtros se guardan en sessionStorage para NO perderlos cuando el
// administrativo edita/ingresa un instrumento, navega a otra pantalla o
// refresca (F5). Así no tiene que volver a filtrar la OS cada vez.
// Se limpian solos al cerrar la pestaña (sessionStorage).
const INGRESOS_FILTERS_KEY = "ingresos_filters_v1";

// Sello de versión visible en la pantalla de Ingresos. Sirve para confirmar de
// un vistazo que el navegador está corriendo el build más nuevo (y no una
// versión vieja cacheada). Actualizar en cada deploy relevante.
const BUILD_TAG = "v4 · buscador";

function loadIngresosFilters() {
  try {
    const raw = sessionStorage.getItem(INGRESOS_FILTERS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}


// --- Cambios críticos (Frontend "password gate") ---
// ⚠️ Esto NO es seguridad fuerte: el valor vive en el frontend compilado.
// Sirve para evitar acciones accidentales por técnicos/usuarios.
const CRITICAL_ADMIN_PASSWORD =
  import.meta?.env?.VITE_CRITICAL_ADMIN_PASSWORD || "CambioCriticoAdmin1998PN-4111";
const CRIT_UNTIL_KEY = "crit_admin_until_v1";
const CRIT_TTL_MS = 2 * 60 * 1000; // 2 minutos

function isCriticalUnlocked() {
  const until = Number(localStorage.getItem(CRIT_UNTIL_KEY) || "0");
  return Date.now() < until;
}
function unlockCritical(password) {
  if (!CRITICAL_ADMIN_PASSWORD) return { ok: false, error: "Falta contraseña crítica." };
  if (password !== CRITICAL_ADMIN_PASSWORD)
    return { ok: false, error: "Contraseña incorrecta." };
  localStorage.setItem(CRIT_UNTIL_KEY, String(Date.now() + CRIT_TTL_MS));
  return { ok: true };
}
function lockCritical() {
  localStorage.removeItem(CRIT_UNTIL_KEY);
}

export default function IngresosPage() {
  const navigate = useNavigate();
  const { isAdministrativeAssistant, hasPermission } = useLabPermissions();
  const canAssignTechnicians = hasPermission(
    ASSISTANT_PERMISSION_KEYS.ASSIGN_TECHNICIANS
  );
  const canRegisterLabEntry = hasPermission(
    ASSISTANT_PERMISSION_KEYS.REGISTER_LAB_ENTRY
  );
  const canManageOperations = hasPermission(
    ASSISTANT_PERMISSION_KEYS.MANAGE_OPERATIONS
  );
  const canChangeInstrumentClient = hasPermission(
    ASSISTANT_PERMISSION_KEYS.CHANGE_INSTRUMENT_CLIENT
  );
  const canSetOsStartSequence = hasPermission(
    ASSISTANT_PERMISSION_KEYS.SET_OS_START_SEQUENCE
  );
  const canDeleteInstruments = hasPermission(
    ASSISTANT_PERMISSION_KEYS.DELETE_INSTRUMENTS
  );
  const canDeletePurchaseOrders = hasPermission(
    ASSISTANT_PERMISSION_KEYS.DELETE_PURCHASE_ORDERS
  );
  const canDeleteRemitos = hasPermission(
    ASSISTANT_PERMISSION_KEYS.DELETE_REMITOS
  );
  const [searchParams] = useSearchParams();
  const focusPoId = searchParams.get("focus");

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Filtros (se restauran desde sessionStorage para no perderlos al editar/navegar/refrescar)
  const initialFilters = useMemo(() => loadIngresosFilters(), []);
  const [q, setQ] = useState(initialFilters.q || "");
  const [clientFilter, setClientFilter] = useState(initialFilters.clientFilter || "");
  const [poStatus, setPoStatus] = useState(initialFilters.poStatus || "");
  const [techFilter, setTechFilter] = useState(initialFilters.techFilter || "");
  const [instrumentIdFilter, setInstrumentIdFilter] = useState(initialFilters.instrumentIdFilter || "");
  const [instrumentTagSerialFilter, setInstrumentTagSerialFilter] = useState(initialFilters.instrumentTagSerialFilter || "");
  const [debouncedQ, setDebouncedQ] = useState(initialFilters.q || "");
  const [debouncedClientFilter, setDebouncedClientFilter] = useState(initialFilters.clientFilter || "");
  const [debouncedInstrumentIdFilter, setDebouncedInstrumentIdFilter] = useState(initialFilters.instrumentIdFilter || "");
  const [debouncedInstrumentTagSerialFilter, setDebouncedInstrumentTagSerialFilter] = useState(initialFilters.instrumentTagSerialFilter || "");
  const [instrumentMatchedPoIds, setInstrumentMatchedPoIds] = useState(null);
  const activeFiltersRef = useRef({
    search: initialFilters.q || "",
    clientSearch: initialFilters.clientFilter || "",
    status: initialFilters.poStatus || "",
  });

  // Datos
  const [poRows, setPoRows] = useState([]);
  // Ref con el largo actual de la lista (los callbacks de realtime capturan
  // closures viejos de loadAll; el ref siempre está al día).
  const poRowsLenRef = useRef(0);
  useEffect(() => {
    poRowsLenRef.current = poRows.length;
  }, [poRows]);
  const [openRows, setOpenRows] = useState({});
  const [poInstruments, setPoInstruments] = useState({});
  const [poInstrumentCounts, setPoInstrumentCounts] = useState({});
  const [techs, setTechs] = useState([]);
  const [poTechnicianName, setPoTechnicianName] = useState({});
  const [unassignedByClient, setUnassignedByClient] = useState({});
  const [pendingIntakeCount, setPendingIntakeCount] = useState(0);

  // Modal asignación de técnico
  const [assigning, setAssigning] = useState({ poId: null, saving: false });
  const [selectedTech, setSelectedTech] = useState("");

  // Scanner QR
  const [scannerValue, setScannerValue] = useState("");
  const [scanningForPo, setScanningForPo] = useState(null);
  const [toast, setToast] = useState(null);
  const [scanErrorModal, setScanErrorModal] = useState(null);

  // Modal QR
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrModalInstrument, setQrModalInstrument] = useState(null);
  const [qrModalClientName, setQrModalClientName] = useState("");
  const [qrModalPoCode, setQrModalPoCode] = useState("");

  // Modal Remitos
  const [remitosModalOpen, setRemitosModalOpen] = useState(false);
  const [remitosModalOS, setRemitosModalOS] = useState(null);
  const [remitosModalInstruments, setRemitosModalInstruments] = useState([]);

  // Intake
  const [createIntakeOpen, setCreateIntakeOpen] = useState(false);
  const [intakeDrawerOpen, setIntakeDrawerOpen] = useState(false);
  const [currentIntakeId, setCurrentIntakeId] = useState(null);
  const [currentIntakeClient, setCurrentIntakeClient] = useState(null);
  const [intakeInstrumentOpen, setIntakeInstrumentOpen] = useState(false);
  const [showPendingIntake, setShowPendingIntake] = useState(false);

  // Vincular ingreso -> PO
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkPo, setLinkPo] = useState(null);

  // Modal de edición de instrumento
  const [editInstrumentModal, setEditInstrumentModal] = useState({
    open: false,
    instrumentId: null,
    poId: null,
    clientName: "",
    servCode: "",
  });

  // Modal de Gestión Operativa
  const [opsModalOpen, setOpsModalOpen] = useState(false);
  const [opsPo, setOpsPo] = useState(null);


  // ✅ Modal cambio crítico: borrar OS/OC (Frontend + contraseña)
  const [adminDeleteOS, setAdminDeleteOS] = useState({
    open: false,
    po: null,
    deleting: false,
  });

  // ✅ Modal cambio crítico: definir número inicial (os_start_seq)
  const [startSeqModal, setStartSeqModal] = useState({
    open: false,
    po: null,
    startSeq: "",
    saving: false,
  });

  // ✅ Modal de contraseña crítica (sin window.prompt)
  const [criticalGate, setCriticalGate] = useState({
    open: false,
    password: "",
    pending: null, // función a ejecutar si valida
    busy: false,
  });


  // Modal de búsqueda general de instrumentos
  const [instrumentSearchOpen, setInstrumentSearchOpen] = useState(false);

  // ✅ Funciones helper para manejar modales con scroll lock
  const openModalWithScrollLock = (setterFn, value = true) => {
    scrollManager.lock();
    setterFn(value);
  };

  const closeModalWithScrollLock = (setterFn, value = false) => {
    setterFn(value);
    setTimeout(() => scrollManager.unlock(), 10);
  };


  // --- Cambio crítico: helpers (modal) ---
  const requestCriticalAccess = (pendingFn) => {
    if (isCriticalUnlocked()) {
      pendingFn?.();
      return;
    }

    scrollManager.lock();
    setCriticalGate({ open: true, password: "", pending: pendingFn || null, busy: false });
  };

  const closeCriticalGate = () => {
    setCriticalGate({ open: false, password: "", pending: null, busy: false });
    setTimeout(() => scrollManager.unlock(), 10);
  };

  const confirmCriticalGate = () => {
    const pass = criticalGate.password || "";
    const res = unlockCritical(pass);
    if (!res.ok) {
      setToast({ message: res.error || "Contraseña incorrecta.", type: "error" });
      return;
    }

      setToast({ message: "Modo critico habilitado (2 min).", type: "success" });
    const fn = criticalGate.pending;
    closeCriticalGate();
    setTimeout(() => fn?.(), 0);
  };

  // --- Cambio crítico: Borrar OS/OC (purchase_order) ---
  const openAdminDeleteOS = (po) => {
    if (isAdministrativeAssistant && canDeletePurchaseOrders) {
      scrollManager.lock();
      setAdminDeleteOS({ open: true, po, deleting: false });
      return;
    }
    requestCriticalAccess(() => {
      scrollManager.lock();
      setAdminDeleteOS({ open: true, po, deleting: false });
    });
  };

  const closeAdminDeleteOS = () => {
    setAdminDeleteOS({ open: false, po: null, deleting: false });
    setTimeout(() => scrollManager.unlock(), 10);
  };

  const confirmAdminDeleteOS = async () => {
    const po = adminDeleteOS.po;
    if (!po?.id) return;

    const ok = window.confirm(
      `Borrar OS/OC ${po.code || po.base_number || ""}

Solo se permite si esta vacia (sin instrumentos).
Confirmas?`
    );
    if (!ok) return;

    try {
      setAdminDeleteOS((p) => ({ ...p, deleting: true }));

      const { count, error: countErr } = await supabase
        .from("service_orders")
        .select("id", { count: "exact", head: true })
        .eq("po_id", po.id);

      if (countErr) throw countErr;
      if ((count || 0) > 0) throw new Error(`No se puede borrar: tiene ${count} instrumentos cargados.`);

      const { error } = await supabase.from("purchase_orders").delete().eq("id", po.id);
      if (error) throw error;

      closeAdminDeleteOS();
      await loadAll();

      setToast({ message: "OS/OC borrada. Puedes regenerarla desde Ofertas.", type: "success" });
    } catch (e) {
      console.error("[confirmAdminDeleteOS]", e);
      setToast({ message: e.message || "No se pudo borrar la OS/OC.", type: "error" });
      setAdminDeleteOS((p) => ({ ...p, deleting: false }));
      setTimeout(() => scrollManager.unlock(), 10);
    }
  };

  // --- Cambio crítico: Definir número inicial (os_start_seq) ---
  const openStartSeqModal = (po) => {
    if (isAdministrativeAssistant && canSetOsStartSequence) {
      scrollManager.lock();
      setStartSeqModal({
        open: true,
        po,
        startSeq: String(po?.os_start_seq ?? 1),
        saving: false,
      });
      return;
    }
    requestCriticalAccess(() => {
      scrollManager.lock();
      setStartSeqModal({
        open: true,
        po,
        startSeq: String(po?.os_start_seq ?? 1),
        saving: false,
      });
    });
  };

  const closeStartSeqModal = () => {
    setStartSeqModal({ open: false, po: null, startSeq: "", saving: false });
    setTimeout(() => scrollManager.unlock(), 10);
  };

  const saveStartSeq = async () => {
    const po = startSeqModal.po;
    const seq = Number(startSeqModal.startSeq);
    if (!po?.id) return;

    if (!Number.isInteger(seq) || seq < 1 || seq > 999) {
      setToast({ message: "Numero inicial invalido (1 a 999).", type: "error" });
      return;
    }

    try {
      setStartSeqModal((p) => ({ ...p, saving: true }));

      const { error } = await supabase
        .from("purchase_orders")
        .update({ os_start_seq: seq })
        .eq("id", po.id);

      if (error) throw error;

      closeStartSeqModal();
      await loadAll();

      setToast({ message: `Inicio actualizado a ${seq}`, type: "success" });
    } catch (e) {
      console.error("[saveStartSeq]", e);
      setToast({ message: e.message || "No se pudo actualizar el inicio.", type: "error" });
      setStartSeqModal((p) => ({ ...p, saving: false }));
      setTimeout(() => scrollManager.unlock(), 10);
    }
  };


  // --- Funciones de data ---
  async function fetchClientIdsForPOFilter(rawTerm) {
    const term = String(rawTerm || "").trim();
    if (!term) return [];

    let query = supabase
      .from("clients")
      .select("id")
      .limit(500);

    const digits = term.replace(/\D/g, "");
    if (/^\d+$/.test(term) && digits) {
      query = query.eq("client_number", Number(digits));
    } else {
      const safeTerm = term.replace(/[%_,]/g, " ");
      const like = `%${safeTerm}%`;
      query = query.or(
        [
          `name.ilike.${like}`,
          `cuit.ilike.${like}`,
          `tax_id.ilike.${like}`,
          `branch_name.ilike.${like}`,
        ].join(",")
      );
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || []).map((row) => row.id).filter(Boolean);
  }

  const fetchPOList = async ({ search = "", clientSearch = "", status = "" } = {}) => {
    let query = supabase
      .from("v_purchase_orders")
      .select(
        "id,code,base_number,status,assigned_to,ops_priority,ops_status,ops_due_date,ops_notes,created_at,offer_id,offer_code,client_id,client_name,instrument_count"
      );

    if (status) query = query.eq("status", status);

    const termRaw = String(search || "").trim();
    if (termRaw) {
      const normalized = termRaw.toLowerCase().replace(/^os[-\s]*/i, "oc-");
      const like = `%${normalized}%`;
      const orParts = [
        `code.ilike.${like}`,
        `offer_code.ilike.${like}`,
        `client_name.ilike.${like}`,
      ];

      const digits = termRaw.replace(/\D/g, "");
      if (digits) {
        orParts.push(`base_number.eq.${Number(digits)}`);
      }

      query = query.or(orParts.join(","));
    }

    const clientTerm = String(clientSearch || "").trim();
    if (clientTerm) {
      const clientIds = await fetchClientIdsForPOFilter(clientTerm);
      if (!clientIds.length) return [];
      query = query.in("client_id", clientIds);
    }

    const { data, error } = await query;
    if (error) throw error;

    // Mostrar OCs en orden descendente por numeración (la más grande primero).
    const list = (data || []).slice().sort((a, b) => {
      const diff = poNumeric(b) - poNumeric(a);
      if (diff !== 0) return diff;
      return String(b.code || "").localeCompare(String(a.code || ""));
    });

    return list;
  };

  const fetchActiveTechs = async () => {
    const { data, error } = await supabase
      .from("technicians")
      .select("id, full_name, active")
      .eq("active", true)
      .order("full_name");
    if (error) throw error;
    return data || [];
  };

  const fetchInstrumentCountsByPO = async (poIds) => {
    // Sin poIds no hay nada que actualizar. NO vaciar el mapa: pisarlo hacía
    // que TODAS las OS mostraran "0 instr." por un instante (y la página
    // cambiara de alto, moviendo el scroll del usuario).
    if (!poIds?.length) return;

    try {
      // Evita consultas enormes con `.in(...)` cuando hay muchas OS.
      const chunkSize = 120;
      const chunks = [];
      for (let i = 0; i < poIds.length; i += chunkSize) {
        chunks.push(poIds.slice(i, i + chunkSize));
      }

      const countsByPo = {};
      for (const poId of poIds) countsByPo[String(poId)] = new Set();

      // Intento principal: usar la misma fuente que la tabla expandida.
      let source = "v_service_orders_enriched";
      for (const idsChunk of chunks) {
        const { data, error } = await supabase
          .from(source)
          .select("po_id, instrument_id")
          .in("po_id", idsChunk)
          .not("instrument_id", "is", null);

        if (error) {
          // Fallback por compatibilidad.
          if (source === "v_service_orders_enriched") {
            source = "service_orders";
            const { data: data2, error: error2 } = await supabase
              .from(source)
              .select("po_id, instrument_id")
              .in("po_id", idsChunk)
              .not("instrument_id", "is", null);
            if (error2) throw error2;
            for (const row of data2 || []) {
              const key = String(row.po_id);
              if (!countsByPo[key]) countsByPo[key] = new Set();
              countsByPo[key].add(String(row.instrument_id));
            }
            continue;
          }
          throw error;
        }

        for (const row of data || []) {
          const key = String(row.po_id);
          if (!countsByPo[key]) countsByPo[key] = new Set();
          countsByPo[key].add(String(row.instrument_id));
        }
      }

      const counts = {};
      for (const poId of poIds) {
        const key = String(poId);
        counts[poId] = countsByPo[key]?.size || 0;
      }
      // MERGE en lugar de reemplazar: cuando se refresca UNA sola OS (p.ej. al
      // cambiar el estado de un instrumento) no hay que borrar los conteos del
      // resto de las órdenes.
      setPoInstrumentCounts((prev) => ({ ...prev, ...counts }));
    } catch (e) {
      console.error("[fetchInstrumentCountsByPO]", e);
      // Conservar lo que ya se mostraba; vaciar el mapa solo empeora la UI.
    }
  };

  const fetchTechnicianNameByPO = async (poIds) => {
    // Sin poIds no hay nada que actualizar (no vaciar el mapa existente).
    if (!poIds?.length) return;

    const { data: osRows, error: osErr } = await supabase
      .from("service_orders")
      .select("po_id, technician_id")
      .in("po_id", poIds);
    if (osErr) throw osErr;

    const { data: poRowsData, error: poErr } = await supabase
      .from("purchase_orders")
      .select("id, assigned_to")
      .in("id", poIds);
    if (poErr) throw poErr;

    const techsByPo = new Map();
    const techIdSet = new Set();
    for (const r of osRows || []) {
      if (!techsByPo.has(r.po_id)) techsByPo.set(r.po_id, new Set());
      if (r.technician_id) {
        techsByPo.get(r.po_id).add(r.technician_id);
        techIdSet.add(r.technician_id);
      }
    }

    const assignedByPo = new Map();
    for (const p of poRowsData || []) {
      if (p.assigned_to) {
        assignedByPo.set(p.id, p.assigned_to);
        techIdSet.add(p.assigned_to);
      }
    }

    if (techIdSet.size === 0) {
      const m = {};
      for (const id of poIds) m[id] = null;
      setPoTechnicianName((prev) => ({ ...prev, ...m }));
      return;
    }

    const { data: techRows, error: techErr } = await supabase
      .from("technicians")
      .select("id, full_name")
      .in("id", Array.from(techIdSet));
    if (techErr) throw techErr;

    const nameById = (techRows || []).reduce((acc, t) => {
      acc[t.id] = t.full_name || "(técnico)";
      return acc;
    }, {});

    const result = {};
    for (const poId of poIds) {
      const assigned = assignedByPo.get(poId);
      if (assigned) {
        result[poId] = nameById[assigned] || "(técnico)";
        continue;
      }
      const set = techsByPo.get(poId) || new Set();
      if (set.size === 0) {
        result[poId] = null;
      } else if (set.size === 1) {
        const onlyId = Array.from(set)[0];
        result[poId] = nameById[onlyId] || "(técnico)";
      } else {
        result[poId] = "Varios";
      }
    }
    // MERGE: al refrescar una sola OS no se pierde el técnico de las demás.
    setPoTechnicianName((prev) => ({ ...prev, ...result }));
  };

  const fetchUnassignedCountsByClient = async (clientIds) => {
    if (!clientIds?.length) {
      setUnassignedByClient({});
      return;
    }

    const map = {};
    try {
      await Promise.all(
        clientIds.map(async (clientId) => {
          if (!clientId) return;
          try {
            const { data, error } = await supabase.rpc(
              "get_unassigned_instruments_for_client",
              { p_client_id: clientId }
            );
            if (error) throw error;
            map[clientId] = (data || []).length;
          } catch (e) {
            console.error("[fetchUnassignedCountsByClient]", clientId, e);
            map[clientId] = 0;
          }
        })
      );
      setUnassignedByClient(map);
    } catch (e) {
      console.error("[fetchUnassignedCountsByClient] general", e);
      setUnassignedByClient({});
    }
  };

  const fetchPendingIntakeCount = async () => {
    try {
      const { count, error } = await supabase
        .from("v_pending_intake_instruments")
        .select("*", { count: "exact", head: true });

      if (error) throw error;
      setPendingIntakeCount(count || 0);
    } catch (e) {
      console.error("[fetchPendingIntakeCount]", e);
      setPendingIntakeCount(0);
    }
  };

  const fetchLatestSoStatusByPO = async (poIds) => {
    if (!poIds?.length) return {};

    const { data, error } = await supabase
      .from("service_orders")
      .select("po_id, status, created_at")
      .in("po_id", poIds)
      .order("po_id", { ascending: true })
      .order("created_at", { ascending: false });
    if (error) throw error;

    const byPo = {};
    for (const row of data || []) {
      if (!row?.po_id) continue;
      if (byPo[row.po_id]) continue;
      byPo[row.po_id] = normalizeServiceOrderStatus(row.status);
    }
    return byPo;
  };

  useEffect(() => {
    activeFiltersRef.current = {
      search: debouncedQ,
      clientSearch: debouncedClientFilter,
      status: poStatus,
    };
  }, [debouncedQ, debouncedClientFilter, poStatus]);

  // Persistir filtros: se guardan en cuanto cambian para restaurarlos al volver.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        INGRESOS_FILTERS_KEY,
        JSON.stringify({
          q,
          clientFilter,
          poStatus,
          techFilter,
          instrumentIdFilter,
          instrumentTagSerialFilter,
        })
      );
    } catch {}
  }, [q, clientFilter, poStatus, techFilter, instrumentIdFilter, instrumentTagSerialFilter]);

  const loadAll = async (filters = {}) => {
    setLoading(true);
    setErr("");
    try {
      const activeFilters = {
        search: filters.search ?? activeFiltersRef.current.search,
        clientSearch: filters.clientSearch ?? activeFiltersRef.current.clientSearch,
        status: filters.status ?? activeFiltersRef.current.status,
      };
      const [poList, techList] = await Promise.all([
        fetchPOList({
          search: activeFilters.search,
          clientSearch: activeFilters.clientSearch,
          status: activeFilters.status,
        }),
        fetchActiveTechs(),
      ]);
      setTechs(techList);

      const poIds = poList.map((p) => p.id);
      const clientIds = Array.from(
        new Set(poList.map((p) => p.client_id).filter(Boolean))
      );

      const latestSoStatusByPo = await fetchLatestSoStatusByPO(poIds);
      const mergedPoList = (poList || []).map((po) => ({
        ...po,
        ops_status: latestSoStatusByPo[po.id] || normalizeServiceOrderStatus(po.ops_status),
      }));

      // 🛡️ Anti-salto de scroll: si la consulta volvió VACÍA sin filtros
      // activos y ya había órdenes en pantalla, es una respuesta espuria
      // (típico: el token de sesión estaba renovándose y RLS filtró todo).
      // Vaciar la lista colapsa la página por un instante y el navegador
      // recorta el scroll al tope. Conservamos lo que hay: el próximo
      // refresh trae los datos reales.
      const hasActiveFilters = !!(
        activeFilters.search ||
        activeFilters.clientSearch ||
        activeFilters.status
      );
      if (!hasActiveFilters && mergedPoList.length === 0 && poRowsLenRef.current > 0) {
        console.warn(
          "[loadAll] Respuesta vacía sospechosa (¿token en refresh?). Se conserva la lista actual."
        );
        return;
      }

      setPoRows(mergedPoList);

      await Promise.all([
        fetchTechnicianNameByPO(poIds),
        fetchInstrumentCountsByPO(poIds),
        fetchUnassignedCountsByClient(clientIds),
        fetchPendingIntakeCount(),
      ]);
    } catch (e) {
      setErr(e.message || "No se pudo cargar la informacion");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedClientFilter(clientFilter), 300);
    return () => clearTimeout(t);
  }, [clientFilter]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        await loadAll();
      } catch (e) {
        if (!cancel) setErr(e.message || "No se pudo cargar la informacion");
      }
    })();
    return () => {
      cancel = true;
    };
  }, [debouncedQ, debouncedClientFilter, poStatus]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedInstrumentIdFilter(instrumentIdFilter);
      setDebouncedInstrumentTagSerialFilter(instrumentTagSerialFilter);
    }, 300);
    return () => clearTimeout(t);
  }, [instrumentIdFilter, instrumentTagSerialFilter]);

  // REALTIME
  useEffect(() => {
    // Debounce: un cambio de estado dispara varios eventos casi simultáneos
    // (instruments + service_orders + purchase_orders). Agruparlos en UN solo
    // loadAll evita recargas en cascada que pisan estados y mueven la página.
    let reloadTimer = null;
    const scheduleReload = () => {
      if (reloadTimer) clearTimeout(reloadTimer);
      reloadTimer = setTimeout(() => {
        reloadTimer = null;
        loadAll();
      }, 400);
    };

    const ch = supabase
      .channel("pos-os-realtime-ingresos")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "service_orders" },
        scheduleReload
      )
      .on(
        "postgres_changes",
        { event: "update", schema: "public", table: "purchase_orders" },
        scheduleReload
      )
      .on(
        "postgres_changes",
        { event: "update", schema: "public", table: "instruments" },
        scheduleReload
      )
      .subscribe();

    return () => {
      if (reloadTimer) clearTimeout(reloadTimer);
      supabase.removeChannel(ch);
    };
  }, []);

  // Auto-expandir con ?focus
  useEffect(() => {
    (async () => {
      if (!focusPoId) return;
      try {
        await ensurePoInstrumentsLoaded(focusPoId);
        setOpenRows((p) => ({ ...p, [focusPoId]: true }));
      } catch {}
    })();
  }, [focusPoId, poRows.length]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const idNeedle = normText(normalizeScanPayload(debouncedInstrumentIdFilter));
      const textNeedle = sanitizeIlikeTerm(debouncedInstrumentTagSerialFilter);
      if (!idNeedle && !textNeedle) {
        setInstrumentMatchedPoIds(null);
        return;
      }

      try {
        let query = supabase
          .from("v_service_orders_enriched")
          .select("po_id")
          .not("po_id", "is", null)
          .limit(5000);

        if (idNeedle) {
          const idNeedleNum = toIntOrNull(idNeedle);
          if (idNeedleNum != null) {
            query = query.eq("instrument_lab_id", idNeedleNum);
          } else if (isUuid(idNeedle)) {
            query = query.eq("instrument_id", idNeedle);
          } else {
            if (!cancelled) setInstrumentMatchedPoIds(new Set());
            return;
          }
        }

        if (textNeedle) {
          const like = `%${textNeedle}%`;
          query = query.or(
            `instrument_tag.ilike.${like},instrument_serial.ilike.${like}`
          );
        }

        const { data, error } = await query;
        if (error) throw error;

        const matched = new Set((data || []).map((row) => row.po_id).filter(Boolean));

        if (!cancelled) setInstrumentMatchedPoIds(matched);
      } catch (e) {
        console.error("[instrumentFilter]", e);
        if (!cancelled) setInstrumentMatchedPoIds(new Set());
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [debouncedInstrumentIdFilter, debouncedInstrumentTagSerialFilter]);

  const techOptions = useMemo(() => {
    const names = Array.from(
      new Set(
        Object.values(poTechnicianName || {})
          .filter(Boolean)
          .filter((n) => n !== "Varios")
      )
    );
    return names.sort((a, b) => String(a).localeCompare(String(b)));
  }, [poTechnicianName]);

  const filteredPOs = useMemo(() => {
    let list = [...(poRows || [])];

    if (techFilter) {
      list = list.filter((po) => {
        const techName = poTechnicianName?.[po.id] || null;
        if (techFilter === "__none") return !techName;
        return techName === techFilter;
      });
    }

    if (instrumentIdFilter || instrumentTagSerialFilter) {
      const matched = instrumentMatchedPoIds || new Set();
      list = list.filter((po) => matched.has(po.id));
    }

    return list;
  }, [
    poRows,
    poTechnicianName,
    techFilter,
    instrumentIdFilter,
    instrumentTagSerialFilter,
    instrumentMatchedPoIds,
  ]);

  // ✅ FUNCIÓN MODIFICADA
  async function fetchInstrumentsByPO(poId) {
    const { data, error } = await supabase
      .from("v_service_orders_enriched")
      .select(
        "service_order_id, instrument_id, instrument_lab_id, instrument_tag, instrument_type, instrument_brand, instrument_model, instrument_serial, code, status, created_at, po_id, po_code, qr_image_url"
      )
      .eq("po_id", poId)
      .order("created_at", { ascending: true });

    if (error) throw error;

    const sorted = (data || []).slice().sort((a, b) => {
      return osNumeric(a.code) - osNumeric(b.code);
    });

    const instrumentIds = Array.from(
      new Set(sorted.map((r) => r.instrument_id).filter(Boolean))
    );

    let clsById = {};
    let placeById = {};

    if (instrumentIds.length) {
      const [{ data: clsRows, error: clsErr }, { data: placeRows, error: placeErr }] =
        await Promise.all([
            supabase
              .from("v_instrument_classification")
              .select("id, category, subcategory")
              .in("id", instrumentIds),
            supabase
            .from("instruments")
            .select("id, calibration_place, reception_date, status, lab_entry_date, technician_notes, rejection_reason, is_broken")
            .in("id", instrumentIds),
        ]);

      if (!clsErr && Array.isArray(clsRows)) {
        clsById = clsRows.reduce((acc, row) => {
          acc[row.id] = {
            category: row.category ?? null,
            subcategory: row.subcategory ?? null,
          };
          return acc;
        }, {});
      }

      if (!placeErr && Array.isArray(placeRows)) {
        placeById = placeRows.reduce((acc, row) => {
          acc[row.id] = {
            calibration_place: row.calibration_place || "Laboratorio",
            reception_date: row.reception_date || null,
            status: row.status || "active",
            lab_entry_date: row.lab_entry_date || null,
            technician_notes: row.technician_notes || null,
            rejection_reason: row.rejection_reason || null,
            is_broken: row.is_broken ?? null,
          };
          return acc;
        }, {});
      }
    }

    const enriched = sorted.map((r) => ({
      ...r,
      instrument_category: clsById[r.instrument_id]?.category ?? null,
      instrument_subcategory: clsById[r.instrument_id]?.subcategory ?? null,
      calibration_place:
        placeById[r.instrument_id]?.calibration_place || "Laboratorio",
      // La fecha pertenece a esta OS, no a la ficha maestra reutilizada.
      reception_date: resolveAdmissionReceptionDate({
        serviceOrderCreatedAt: r.created_at,
        legacyReceptionDate: placeById[r.instrument_id]?.reception_date,
      }),
      instrument_status: resolveAdmissionStatus({
        instrumentStatus: placeById[r.instrument_id]?.status || "active",
        serviceOrderStatus: r.status,
        // Al crear una nueva OS, un estado terminal del maestro pertenece a la
        // visita anterior hasta que el ciclo actual registre su primer cambio.
        hasCurrentCycleTransition: !isPreviousCycleStatus(placeById[r.instrument_id]?.status),
      }),
      lab_entry_date: placeById[r.instrument_id]?.lab_entry_date || null,
      technician_notes: placeById[r.instrument_id]?.technician_notes || null,
      rejection_reason: placeById[r.instrument_id]?.rejection_reason || null,
      is_broken: placeById[r.instrument_id]?.is_broken ?? null,
    }));

    setPoInstruments((p) => ({ ...p, [poId]: enriched }));
    await fetchInstrumentCountsByPO([poId]);

    return enriched;
  }

  async function handleInstrumentUpdated(updatedInstrument) {
    // El modal todavía tiene el scroll bloqueado en este punto (scrollY≈0).
    // Anclamos recién después de que el onClose del modal restaure la
    // posición real (unlock corre ~10ms después).
    const anchor = {
      instrumentId: editInstrumentModal.instrumentId,
      poId: editInstrumentModal.poId,
    };
    setTimeout(() => pinScrollAnchor(anchor, 4000), 80);
    try {
      if (editInstrumentModal.poId) {
        await fetchInstrumentsByPO(editInstrumentModal.poId);
      }
      setToast({
        message: "Instrumento actualizado correctamente.",
        type: "success",
      });
    } catch (e) {
      console.error("[handleInstrumentUpdated]", e);
      setToast({
        message: e.message || "No se pudo actualizar la tabla de instrumentos",
        type: "error",
      });
    } finally {
      setEditInstrumentModal({ open: false, instrumentId: null, poId: null, clientName: "" });
    }
  }

  async function handleInstrumentStatusUpdated(row, po, nextStatus) {
    if (!row?.instrument_id || !po?.id || !nextStatus) return;
    // Clavar la fila del instrumento en su lugar durante el refresh posterior.
    pinScrollAnchor({ instrumentId: row.instrument_id, poId: po.id }, 4000);
    try {
      const { error } = await supabase
        .from("instruments")
        .update({ status: nextStatus })
        .eq("id", row.instrument_id);

      if (error) throw error;

      await fetchInstrumentsByPO(po.id);
      setToast({
        message: "Estado del instrumento actualizado.",
        type: "success",
      });
    } catch (e) {
      console.error("[handleInstrumentStatusUpdated]", e);
      setToast({
        message: e.message || "No se pudo actualizar el estado del instrumento.",
        type: "error",
      });
    }
  }

  async function handleAllInstrumentStatusesUpdated(rows, po, nextStatus) {
    const instrumentIds = Array.from(
      new Set((rows || []).map((row) => row?.instrument_id).filter(Boolean))
    );
    if (!instrumentIds.length || !po?.id || !nextStatus) return;

    pinScrollAnchor({ instrumentId: instrumentIds[0], poId: po.id }, 4000);
    try {
      const { error } = await supabase
        .from("instruments")
        .update({ status: nextStatus })
        .in("id", instrumentIds);

      if (error) throw error;

      await fetchInstrumentsByPO(po.id);
      setToast({
        message: `Se actualizaron ${instrumentIds.length} instrumento${
          instrumentIds.length === 1 ? "" : "s"
        } de una sola vez.`,
        type: "success",
      });
    } catch (e) {
      console.error("[handleAllInstrumentStatusesUpdated]", e);
      setToast({
        message: e.message || "No se pudieron actualizar todos los instrumentos.",
        type: "error",
      });
      throw e;
    }
  }

  async function ensurePoInstrumentsLoaded(poId) {
    if (poInstruments[poId]) return poInstruments[poId];
    return await fetchInstrumentsByPO(poId);
  }

  const toggleRow = async (poId) => {
    const isOpen = !!openRows[poId];

    if (isOpen) {
      setOpenRows((p) => ({ ...p, [poId]: false }));
      return;
    }

    try {
      await ensurePoInstrumentsLoaded(poId);
      setOpenRows((p) => ({ ...p, [poId]: true }));
    } catch (e) {
      console.error(e);
      alert(e.message || "No se pudieron cargar los instrumentos de la OS.");
    }
  };

  // Scanner QR
  const startScanning = async (poId) => {
    try {
      await ensurePoInstrumentsLoaded(poId);
      setOpenRows((p) => ({ ...p, [poId]: true }));
      setScanningForPo(poId);
      setScannerValue("");
    } catch (e) {
      setToast({
        message: e.message || "Error al preparar el escaneo",
        type: "error",
      });
    }
  };

  const stopScanning = () => {
    setScanningForPo(null);
    setScannerValue("");
  };

  const handleScannerKeyDown = async (e) => {
    if (e.key !== "Enter" || !scannerValue.trim() || !scanningForPo) return;

    const code = normalizeScanPayload(scannerValue);
    setScannerValue("");

    if (!code) return;

    try {
      const { data, error } = await supabase.rpc("add_instrument_to_po_scan", {
        p_po_id: scanningForPo,
        p_code: code,
      });

      if (error) {
        console.error("RPC error (add_instrument_to_po_scan):", error);
        const msg = error.message || "Error al agregar por QR";
        const lower = msg.toLowerCase();
        if (lower.includes("cliente distinto") ||
          lower.includes("vincular este instrumento a la orden")
        ) {
          scrollManager.lock(); // 🔒 Bloquear scroll antes de mostrar modal
          setScanErrorModal(msg);
        } else {
          setToast({
            message: msg,
            type: "error",
          });
        }
        return;
      }

      setToast({
        message: `Instrumento ${code} agregado a la OS.`,
        type: "success",
      });

      const { error: seqError } = await supabase.rpc("resequence_os_codes_for_po", {
        p_po_id: scanningForPo,
      });
      if (seqError) {
        console.error("Error al re-secuenciar OS (tras agregar):", seqError.message);
      }

      await fetchInstrumentsByPO(scanningForPo);
      await loadAll();
    } catch (e) {
      console.error(e);
      const msg = e.message || "Error al procesar el código";
      const lower = msg.toLowerCase();
      if (
        lower.includes("cliente distinto") ||
        lower.includes("vincular este instrumento a la orden")
      ) {
        scrollManager.lock(); // 🔒 Bloquear scroll antes de mostrar modal
        setScanErrorModal(msg);
      } else {
        setToast({
          message: msg,
          type: "error",
        });
      }
    }
  };

  // Asignación de técnico
  const openAssign = (poId) => {
    scrollManager.lock(); // 🔒 Bloquear scroll primero
    setAssigning({ poId, saving: false });
    setSelectedTech("");
  };

  const closeAssign = () => {
    setAssigning({ poId: null, saving: false });
    setTimeout(() => scrollManager.unlock(), 10); // 🔓 Desbloquear después
  };

  const doAssign = async () => {
    if (!assigning.poId || !selectedTech) return;
    try {
      setAssigning((p) => ({ ...p, saving: true }));

      const { error: rpcErr } = await supabase.rpc("assign_po_to_technician", {
        p_po_id: assigning.poId,
        p_technician_id: selectedTech,
      });
      if (rpcErr) throw rpcErr;

      const { data: po, error: qErr } = await supabase
        .from("purchase_orders")
        .select("id, status")
        .eq("id", assigning.poId)
        .single();
      if (!qErr && po?.status === "unassigned") {
        const { error: updErr } = await supabase
          .from("purchase_orders")
          .update({ status: "assigned" })
          .eq("id", assigning.poId);
        if (updErr) console.warn("[Ingresos] Update estado OS:", updErr.message);
      }

      await fetchInstrumentsByPO(assigning.poId);
      await loadAll();
      await fetchTechnicianNameByPO([assigning.poId]);

      closeAssign();
      setToast({
        message: "Tecnico asignado correctamente",
        type: "success",
      });
    } catch (e) {
      console.error(e);
      setToast({
        message: e.message || "No se pudo asignar el técnico",
        type: "error",
      });
      setAssigning((p) => ({ ...p, saving: false }));
    }
  };

  // Modal QR
  async function openQRModalForRow(row, context) {
    const base = {
      id: row.instrument_id,
      labId: row.instrument_lab_id,
      tag: row.instrument_tag,
      type: row.instrument_type,
      qrImageUrl: row.qr_image_url || row.instrument_qr_url || null,
      poId: row.po_id,
    };

    scrollManager.lock(); // 🔒 Bloquear scroll primero
    setQrModalInstrument(base);
    setQrModalClientName(context?.clientName || "");
    setQrModalPoCode(context?.poCode || "");
    setQrModalOpen(true);
  }

  async function handleDeleteInstrument(row, po) {
    const ok = window.confirm(
      `Seguro que queres quitar el instrumento "${row.instrument_tag || row.instrument_lab_id || ""}" de la OS ${row.code}?`
    );
    if (!ok) return;

    try {
      const { error } = await supabase.rpc("unlink_instrument_from_po", {
        p_os_id: row.service_order_id,
        p_instrument_id: row.instrument_id,
      });
      if (error) throw error;

      await fetchInstrumentsByPO(po.id);
      await loadAll();
      setOpenRows((prev) => ({ ...prev, [po.id]: true }));

      setToast({
        message: "Instrumento quitado de la orden de servicio.",
        type: "success",
      });
    } catch (e) {
      console.error("[handleDeleteInstrument]", e);
      setToast({
        message: e.message || "No se pudo quitar el instrumento.",
        type: "error",
      });
    }
  }

  // Modal Remitos
  async function openRemitosModal(po) {
    const instrumentsRaw = await ensurePoInstrumentsLoaded(po.id);

    const mappedInstruments = (instrumentsRaw || []).map((r) => ({
      id: r.instrument_id,
      tag: r.instrument_tag || "",
      brand: r.instrument_brand || "",
      model: r.instrument_model || "",
      serial: r.instrument_serial || "",
    }));

    scrollManager.lock(); // 🔒 Bloquear scroll primero
    setRemitosModalOS({
      id: po.id,
      code: po.code || `OS-${po.base_number}`,
      number: po.base_number,
      client_id: po.client_id,
    });
    setRemitosModalInstruments(mappedInstruments);

    setRemitosModalOpen(true);
  }

  const openOperations = (po) => {
    scrollManager.lock(); // 🔒 Bloquear scroll primero
    setOpsPo(po);
    setOpsModalOpen(true);
  };

  const closeOperations = () => {
    setOpsPo(null);
    setOpsModalOpen(false);
    setTimeout(() => scrollManager.unlock(), 10); // 🔓 Desbloquear después
  };

  const handleLabEntry = async (po) => {
    if (!po?.id) return;
    const today = new Date().toISOString().slice(0, 10);
    try {
      // instruments no tiene po_id directo; se vinculan via service_orders
      const { data: soRows, error: soErr } = await supabase
        .from("service_orders")
        .select("instrument_id")
        .eq("po_id", po.id);
      if (soErr) throw soErr;

      const instrIds = (soRows || []).map((r) => r.instrument_id).filter(Boolean);
      if (instrIds.length > 0) {
        const { error: instrErr } = await supabase
          .from("instruments")
          .update({ lab_entry_date: today })
          .in("id", instrIds)
          .is("lab_entry_date", null);
        if (instrErr) throw instrErr;
      }

      const { error: poErr } = await supabase
        .from("purchase_orders")
        .update({ ops_status: "delivered_to_lab" })
        .eq("id", po.id);
      if (poErr) throw poErr;
    } catch (e) {
      console.error("[handleLabEntry]", e);
      alert("No se pudo registrar la bajada al lab: " + (e.message || e));
    }
  };

  // --- Modales de cambio crítico ---
  const AdminDeleteOSModal = () => {
    if (!adminDeleteOS.open) return null;
    const po = adminDeleteOS.po;

    return (
      <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60">
        <div className="w-full max-w-md rounded-xl border border-white/10 bg-slate-950 p-4 shadow-2xl">
          <div className="text-lg font-semibold text-white">Borrar OS/OC (Cambio critico)</div>
          <div className="mt-1 text-sm text-white/70">
            OS/OC: <b>{po?.code || po?.base_number || po?.id}</b>
            <br />
            Solo se permite si esta vacia (sin instrumentos).
          </div>

          <div className="mt-4 flex items-center justify-end gap-2">
            <button
              type="button"
              className="rounded-md border border-white/15 px-3 py-2 text-white/80 hover:bg-white/10"
              onClick={closeAdminDeleteOS}
              disabled={adminDeleteOS.deleting}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="rounded-md border border-red-500/60 px-3 py-2 text-red-200 hover:bg-red-500/10"
              onClick={confirmAdminDeleteOS}
              disabled={adminDeleteOS.deleting}
            >
              {adminDeleteOS.deleting ? "Borrando..." : "🗑️ Borrar"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const StartSeqModal = () => {
    if (!startSeqModal.open) return null;
    const po = startSeqModal.po;

    return (
      <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60">
        <div className="w-full max-w-md rounded-xl border border-white/10 bg-slate-950 p-4 shadow-2xl">
          <div className="text-lg font-semibold text-white">Definir numero inicial</div>
          <div className="mt-1 text-sm text-white/70">
            OS/OC: <b>{po?.code || po?.base_number || po?.id}</b>
            <br />
            Ej: 40 -&gt; 040, 041, 042...
          </div>

          <label className="mt-4 block text-sm text-white/80">Numero inicial</label>
          <input
            type="number"
            min="1"
            max="999"
            value={startSeqModal.startSeq}
            onChange={(e) => setStartSeqModal((p) => ({ ...p, startSeq: e.target.value }))}
            className="mt-1 w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-white outline-none focus:border-white/30"
            placeholder="Ej: 40"
            autoFocus
          />

          <div className="mt-4 flex items-center justify-end gap-2">
            <button
              type="button"
              className="rounded-md border border-white/15 px-3 py-2 text-white/80 hover:bg-white/10"
              onClick={closeStartSeqModal}
              disabled={startSeqModal.saving}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="rounded-md border border-emerald-500/60 px-3 py-2 text-emerald-200 hover:bg-emerald-500/10"
              onClick={saveStartSeq}
              disabled={startSeqModal.saving}
            >
              {startSeqModal.saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    );
  };


  return (
    <div className="max-w-7xl mx-auto px-4 pb-16">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* ✅ Modal de contraseña crítica (input type=password) */}
      {criticalGate.open && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60">
          <div className="w-full max-w-md rounded-xl border border-white/10 bg-slate-950 p-5 shadow-2xl mx-4">
            <div className="text-lg font-semibold text-white">Confirmacion requerida</div>
            <div className="mt-1 text-sm text-white/70">
              Ingresa la <b>contrasena de cambio critico</b> para continuar.
            </div>

            <label className="mt-4 block text-sm text-white/80">Contrasena</label>
            <input
              autoFocus
              type="password"
              value={criticalGate.password}
              onChange={(e) => setCriticalGate((p) => ({ ...p, password: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === "Enter") confirmCriticalGate();
                if (e.key === "Escape") closeCriticalGate();
              }}
              className="mt-1 w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-white outline-none focus:border-white/30"
              placeholder="**********"
            />
            <div className="mt-2 text-xs text-white/60">
              El acceso dura <b>2 minutos</b>.
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-md border border-white/15 px-3 py-2 text-white/80 hover:bg-white/10"
                onClick={closeCriticalGate}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="rounded-md border border-emerald-500/50 px-3 py-2 text-emerald-200 hover:bg-emerald-500/10"
                onClick={confirmCriticalGate}
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}

      {scanErrorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-slate-900 border border-red-500/60 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-lg font-semibold mb-2 text-red-300">Atencion</h2>
            <p className="text-sm text-slate-100 mb-4">{scanErrorModal}</p>
            <button
              className="w-full mt-2 px-4 py-2 rounded-md bg-red-600 hover:bg-red-500 text-white text-sm font-medium"
              onClick={() => {
                setScanErrorModal(null);
                setTimeout(() => scrollManager.unlock(), 10);
              }}
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      <header className="flex flex-col gap-6 py-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">
              Ingresos
              <span className="ml-2 align-middle text-[10px] font-semibold uppercase tracking-wide text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5">
                {BUILD_TAG}
              </span>
            </h1>
            <p className="text-sm text-muted-foreground">
              Registrar instrumentos a su Orden de Servicio (OS) y asignarla a un técnico.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              className="px-4 py-2 rounded-md bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium shadow-lg transition-transform active:scale-95 whitespace-nowrap"
              onClick={() => openModalWithScrollLock(setInstrumentSearchOpen, true)}
            >
              🔍 Buscar instrumentos
            </button>

            <button
              className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium shadow-lg transition-transform active:scale-95 whitespace-nowrap"
              onClick={() => openModalWithScrollLock(setCreateIntakeOpen, true)}
            >
              + Nuevo ingreso
            </button>
          </div>
        </div>

        <InstrumentQuickSearch />

        <div className="w-full">
          <POFilters
            q={q}
            setQ={setQ}
            clientFilter={clientFilter}
            setClientFilter={setClientFilter}
            poStatus={poStatus}
            setPoStatus={setPoStatus}
            techFilter={techFilter}
            setTechFilter={setTechFilter}
            techOptions={techOptions}
            instrumentIdFilter={instrumentIdFilter}
            setInstrumentIdFilter={setInstrumentIdFilter}
            instrumentTagSerialFilter={instrumentTagSerialFilter}
            setInstrumentTagSerialFilter={setInstrumentTagSerialFilter}
          />
        </div>
      </header>

      {err && (
        <div className="mb-4 rounded border border-red-900/40 bg-red-900/10 p-3 text-sm text-red-300">
          {err}
        </div>
      )}

      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-medium">Órdenes para ingreso</h2>
            <Badge color="sky">{filteredPOs.length}</Badge>
          </div>

          <div className="flex items-center gap-3">
            <button
              className="px-3 py-1.5 rounded-md border border-slate-500 hover:bg-slate-800 text-slate-300 hover:text-white text-xs font-medium transition-transform active:scale-95"
              onClick={() => navigate("/ingresos/historial")}
            >
              Historial de intakes
            </button>

            <button
              className="px-3 py-1.5 rounded-md bg-indigo-600/90 hover:bg-indigo-500 text-white text-xs font-medium flex items-center gap-2 shadow-sm transition-transform active:scale-95"
              onClick={() => openModalWithScrollLock(setShowPendingIntake, true)}
              title="Ver instrumentos cargados en intake pero sin asignar OS"
            >
              <span className="text-sm">📋</span>
              <span>Ver Intake</span>
              {pendingIntakeCount > 0 && (
                <span className="inline-flex items-center justify-center bg-white text-indigo-600 font-bold text-[10px] rounded-full px-1.5 h-3.5 min-w-[1rem]">
                  {pendingIntakeCount}
                </span>
              )}
            </button>
          </div>
        </div>

        <POTable
          canAssignTechnicians={canAssignTechnicians}
          canRegisterLabEntry={canRegisterLabEntry}
          canManageOperations={canManageOperations}
          canSetOsStartSequence={canSetOsStartSequence}
          canDeleteInstruments={canDeleteInstruments}
          canDeletePurchaseOrders={canDeletePurchaseOrders}
          filteredPOs={filteredPOs}
          poTechnicianName={poTechnicianName}
          poInstrumentCounts={poInstrumentCounts}
          openRows={openRows}
          scanningForPo={scanningForPo}
          poInstruments={poInstruments}
          loading={loading}
          scannerValue={scannerValue}
          setScannerValue={setScannerValue}
          onToggleRow={toggleRow}
          onStartScanning={startScanning}
          onStopScanning={stopScanning}
          onOpenAssign={openAssign}
          onOpenRemitos={openRemitosModal}
          onScannerKeyDown={handleScannerKeyDown}
          onOpenQRModal={openQRModalForRow}
          onRefreshInstruments={async () => {
            if (scanningForPo) {
              await fetchInstrumentsByPO(scanningForPo);
              await loadAll();
            }
          }}
          onLinkIntake={(po) => {
            scrollManager.lock(); // 🔒 Bloquear scroll primero
            setLinkPo(po);
            setLinkModalOpen(true);
          }}
          unassignedByClient={unassignedByClient}
          onEditInstrument={(row, po) => {
            scrollManager.lock(); // 🔒 Bloquear scroll primero
            setEditInstrumentModal({
              open: true,
              instrumentId: row.instrument_id,
              poId: po.id,
              clientName: po?.client_name || "",
              servCode: row?.code || "",
            });
          }}
          onDeleteInstrument={handleDeleteInstrument}
          onUpdateInstrumentStatus={handleInstrumentStatusUpdated}
          onUpdateAllInstrumentStatuses={handleAllInstrumentStatusesUpdated}
          onOpenOperations={openOperations}
          onSetStartSeq={openStartSeqModal}
          onAdminDeleteOS={openAdminDeleteOS}
          onLabEntry={handleLabEntry}
        />

        <p className="text-xs text-slate-400 mt-2">
          * "Ingresar instrumento" abre el formulario y pasa <code>?poId=...</code> en la URL. Al
          guardar, la OS se crea para ese instrumento.
        </p>
        <p className="text-xs text-slate-400 mt-1">
          * "Escanear QR" activa el modo de lectura de codigos para agregar instrumentos
          directamente usando el lector o lab_id/UUID.
        </p>
      </section>

      <AssignTechnicianModal
        assigning={assigning}
        techs={techs}
        selectedTech={selectedTech}
        setSelectedTech={setSelectedTech}
        onClose={closeAssign}
        onAssign={doAssign}
      />

      {remitosModalOpen && remitosModalOS && (
        <RemitosModal
          open={remitosModalOpen}
          os={remitosModalOS}
          instrumentsOfOS={remitosModalInstruments}
          setToast={setToast}
          canDeleteRemitos={canDeleteRemitos}
          onClose={() => {
            setRemitosModalOpen(false);
            setRemitosModalOS(null);
            setRemitosModalInstruments([]);
            setTimeout(() => scrollManager.unlock(), 10); // 🔓 Desbloquear después
          }}
        />
      )}

      {qrModalOpen && qrModalInstrument && (
        <QRLabelModal
          open={qrModalOpen}
          instrument={qrModalInstrument}
          clientName={qrModalClientName}
          poCode={qrModalPoCode}
          onClose={() => {
            setQrModalOpen(false);
            setQrModalInstrument(null);
            setTimeout(() => scrollManager.unlock(), 10); // 🔓 Desbloquear después
          }}
        />
      )}

      <CreateIntakeModal
        open={createIntakeOpen}
        onClose={() => closeModalWithScrollLock(setCreateIntakeOpen, false)}
        onCreated={(intakeId, client) => {
          setCreateIntakeOpen(false);
          setCurrentIntakeId(intakeId);
          setCurrentIntakeClient(client);
          // No desbloquear aquí porque vamos a abrir otro modal
          setIntakeInstrumentOpen(true);
        }}
      />

      <IntakeDrawer
        open={intakeDrawerOpen}
        onClose={() => closeModalWithScrollLock(setIntakeDrawerOpen, false)}
        intakeId={currentIntakeId}
        client={currentIntakeClient}
      />

      <IntakeInstrumentModal
        open={intakeInstrumentOpen}
        onClose={() => {
          setIntakeInstrumentOpen(false);
          setTimeout(() => scrollManager.unlock(), 10); // 🔓 Desbloquear después
        }}
        intakeId={currentIntakeId}
        client={currentIntakeClient}
      />

      <IntakeInstrumentModal
        open={editInstrumentModal.open}
        onClose={() => {
          setEditInstrumentModal({ open: false, instrumentId: null, poId: null, clientName: "", servCode: "" });
          setTimeout(() => scrollManager.unlock(), 10); // 🔓 Desbloquear después
        }}
        mode="edit"
        instrumentId={editInstrumentModal.instrumentId}
        servCode={editInstrumentModal.servCode}
        client={{ name: editInstrumentModal.clientName }}
        onSaved={handleInstrumentUpdated}
        criticalUnlocked={
          isCriticalUnlocked() ||
          (isAdministrativeAssistant && canChangeInstrumentClient)
        }
        onRequestCriticalAccess={
          isAdministrativeAssistant
            ? undefined
            : (pendingFn) => requestCriticalAccess(pendingFn)
        }
        canChangeClient={canChangeInstrumentClient}
      />

      <LinkIntakeToPOModal
        open={linkModalOpen}
        po={linkPo}
        onClose={() => {
          setLinkModalOpen(false);
          setTimeout(() => scrollManager.unlock(), 10); // 🔓 Desbloquear después
        }}
        onLinked={async () => {
          setLinkModalOpen(false);
          setLinkPo(null);
          await loadAll();
          setTimeout(() => scrollManager.unlock(), 10); // 🔓 Desbloquear después
        }}
      />

      <OperationsModal po={opsPo} open={opsModalOpen} onClose={closeOperations} onSaved={loadAll} />

      {showPendingIntake && (
        <PendingIntakeInstrumentsModal
          open={showPendingIntake}
          onClose={() => closeModalWithScrollLock(setShowPendingIntake, false)}
        />
      )}

      <StartSeqModal />
      <AdminDeleteOSModal />

      <InstrumentSearchModal
        open={instrumentSearchOpen}
        onClose={() => closeModalWithScrollLock(setInstrumentSearchOpen, false)}
        canDelete={canDeleteInstruments}
        canChangeClient={canChangeInstrumentClient}
        criticalUnlocked={
          isCriticalUnlocked() ||
          (isAdministrativeAssistant && canChangeInstrumentClient)
        }
        onRequestCriticalAccess={
          isAdministrativeAssistant
            ? undefined
            : (pendingFn) => requestCriticalAccess(pendingFn)
        }
      />
    </div>
  );
}

